import React, { useRef, useEffect } from "react";
import gsap from "gsap";
import { Col, Container, Row } from "react-bootstrap";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import useOnScreen from "./use-on-screen"; // <- import your useOnScreen hook

const Gallery2 = ({ images }) => {
	// Ensure there are exactly 4 images
	const [topLeft, topRight1, topRight2, bottomRight] = images;

	// Gatsby images
	const getGatsbyImage = (image) =>
		image?.localFile?.childImageSharp?.gatsbyImageData
			? getImage(image.localFile.childImageSharp.gatsbyImageData)
			: null;

	// --- 1) Hook up our container to detect when it's in view
	const containerRef = useRef(null);
	const isVisible = useOnScreen(containerRef, "0px");

	// --- 2) Animate once visible
	useEffect(() => {
		if (isVisible) {
			const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
			// Animate .gallery-animate-item from "y=50px, opacity=0" to final
			tl.to(".gallery-animate-item", {
				y: 0,
				opacity: 1,
				duration: 0.8,
				stagger: 0.2,
				delay: 1,
			});
		}
	}, [isVisible]);

	return (
		<section className="py-5 py-lg-7" ref={containerRef}>
			{/* -- Inline style to define initial hidden states -- */}
			<style>
				{`
          .gallery-animate-item {
            opacity: 0;
            transform: translateY(50px);
          }
        `}
			</style>

			<Container>
				<Row className="gx-4 gy-4 d-none d-md-flex">
					{/* Top Left Square Image */}
					<Col md={6}>
						<div className="d-flex align-items-stretch">
							<div
								className="overflow-hidden w-100 gallery-animate-item"
								style={{ aspectRatio: "1 / 1", borderRadius: "12px" }}
							>
								{getGatsbyImage(topLeft.image) && (
									<GatsbyImage
										image={getGatsbyImage(topLeft.image)}
										alt={topLeft.image.altText || "Gallery Image"}
										className="h-100 w-100"
										style={{ objectFit: "cover" }}
									/>
								)}
							</div>
						</div>
						<div
							className="overflow-hidden w-100 mt-4 gallery-animate-item"
							style={{ aspectRatio: "16 / 9", borderRadius: "12px" }}
						>
							{getGatsbyImage(topRight1.image) && (
								<GatsbyImage
									image={getGatsbyImage(topRight1.image)}
									alt={topRight1.image.altText || `Gallery Image`}
									className="h-100 w-100"
									style={{ objectFit: "cover" }}
								/>
							)}
						</div>
					</Col>

					{/* Top Right Landscape Images */}
					<Col md={6}>
						<div
							className="overflow-hidden w-100 mb-4 gallery-animate-item"
							style={{ aspectRatio: "16 / 9", borderRadius: "12px" }}
						>
							{getGatsbyImage(topRight2.image) && (
								<GatsbyImage
									image={getGatsbyImage(topRight2.image)}
									alt={topRight2.image.altText || `Gallery Image`}
									className="h-100 w-100"
									style={{ objectFit: "cover" }}
								/>
							)}
						</div>
						<div
							className="overflow-hidden w-100 gallery-animate-item"
							style={{ aspectRatio: "1 / 1", borderRadius: "12px" }}
						>
							{getGatsbyImage(bottomRight.image) && (
								<GatsbyImage
									image={getGatsbyImage(bottomRight.image)}
									alt={bottomRight.image.altText || "Gallery Image"}
									className="h-100 w-100"
									style={{ objectFit: "cover" }}
								/>
							)}
						</div>
					</Col>
				</Row>

				{/* Mobile Layout */}
				<Row className="gx-4 gy-4 d-md-none">
					<Col>
						<Row className="justify-content-end">
							<Col xs={12}>
								<div className="d-flex align-items-stretch">
									<div
										className="overflow-hidden w-100 gallery-animate-item"
										style={{ aspectRatio: "1 / 1", borderRadius: "12px" }}
									>
										{getGatsbyImage(topLeft.image) && (
											<GatsbyImage
												image={getGatsbyImage(topLeft.image)}
												alt={topLeft.image.altText || "Gallery Image"}
												className="h-100 w-100"
												style={{ objectFit: "cover" }}
											/>
										)}
									</div>
								</div>
							</Col>
							<Col xs={8}>
								<div
									className="overflow-hidden w-100 mt-4 gallery-animate-item"
									style={{ aspectRatio: "3 / 2", borderRadius: "12px" }}
								>
									{getGatsbyImage(topRight1.image) && (
										<GatsbyImage
											image={getGatsbyImage(topRight1.image)}
											alt={topRight1.image.altText || `Gallery Image`}
											className="h-100 w-100"
											style={{ objectFit: "cover" }}
										/>
									)}
								</div>
							</Col>
						</Row>
						<Row className="justify-content-start mt-4">
							<Col xs={12}>
								<div
									className="overflow-hidden w-100 gallery-animate-item"
									style={{ aspectRatio: "1 / 1", borderRadius: "12px" }}
								>
									{getGatsbyImage(bottomRight.image) && (
										<GatsbyImage
											image={getGatsbyImage(bottomRight.image)}
											alt={bottomRight.image.altText || "Gallery Image"}
											className="h-100 w-100"
											style={{ objectFit: "cover" }}
										/>
									)}
								</div>
							</Col>
							<Col xs={8}>
								<div
									className="overflow-hidden w-100 mt-4 gallery-animate-item"
									style={{ aspectRatio: "3 / 2", borderRadius: "12px" }}
								>
									{getGatsbyImage(topRight2.image) && (
										<GatsbyImage
											image={getGatsbyImage(topRight2.image)}
											alt={topRight2.image.altText || `Gallery Image`}
											className="h-100 w-100"
											style={{ objectFit: "cover" }}
										/>
									)}
								</div>
							</Col>
						</Row>
					</Col>
				</Row>
			</Container>
		</section>
	);
};

export default Gallery2;
